import {
  BMButton,
  BMComponentsType,
  BMPrimitives,
  BMSkeletonLoader,
  BMText,
  useBMTheme,
} from "@traveloka/district-bloom";
import {
  ComponentProps,
  Fragment,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView,
  StyleSheet,
} from "react-native";

import { useSafeAreaInsets } from "react-native-safe-area-context";
import { FullScreenDialog } from "../../../../components";
import BMView from "../../../../components/View/BMView";
import { useMobileContentResource } from "../../../../core/resource/MobileResourceProvider";
import { ContentGroup } from "../../../../core/resource/types";
import { convert, formatCurrency, formatMessage } from "../../../../utils";
import { getRefundableStatus } from "../../Flight/FlightRefundInfos";
import { FlightSummary } from "../../Flight/FlightSummary";
import {
  AdultPassengerFormValue,
  BaggageAddOns,
  ChildPassengerFormValue,
  FlightPassengerAddOns,
  FlightPrebookFormValue,
  InfantPassengerFormValue,
} from "../types";
import Header from "./components/Header";
import ModalCompliantConfirmation from "./components/ModalCompliantConfirmation";
import PassengerSummaryItem from "./components/PassengerSummaryItem";
import { FlightPrebookReviewModalComponentProps } from "./types";

const SCROLL_OFFSET_DROP_SHADOW_THRESHOLD = 10;

export default function FlightPrebookReviewModal({
  visible,
  summaries,
  refundInfoData,
  isSubmittingBooking,
  onClose,
  onConfirm = () => {},
}: FlightPrebookReviewModalComponentProps) {
  const insets = useSafeAreaInsets();
  const { BMComponents } = useBMTheme();
  const styles = useMemo(() => createStyles(BMComponents), [BMComponents]);

  const [showNonCompliantModal, setShowNonCompliantModal] = useState(false);
  const { CorporateFlightGeneral: cr, CorporateFlightPrebookForm: crf } =
    useMobileContentResource();

  const { control, getValues } = useFormContext<FlightPrebookFormValue>();
  const priceYouPay = useWatch({
    control,
    name: "totalFareFromClient",
  });

  const [animate, setAnimate] = useState(false);
  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const yOffset = event.nativeEvent.contentOffset.y;
      setAnimate(yOffset > SCROLL_OFFSET_DROP_SHADOW_THRESHOLD);
    },
    []
  );

  const handleConfirm = useCallback(() => {
    if (summaries.some((summary) => !summary.isCompliant)) {
      setShowNonCompliantModal(true);
    } else {
      onConfirm();
    }
  }, [summaries, onConfirm]);

  const handleConfirmNonCompliant = useCallback(() => {
    setShowNonCompliantModal(false);
    onConfirm();
  }, [onConfirm]);

  const refundableByIndex = useMemo(
    () =>
      summaries?.map((_, i) => {
        if (!refundInfoData && i < (summaries?.length ?? 0) - 1)
          return undefined;
        const status = refundInfoData
          ? getRefundableStatus(refundInfoData[i])
          : undefined;
        return { status } as ComponentProps<typeof FlightSummary>["refundable"];
      }) ?? [],
    [summaries, refundInfoData]
  );

  const productSummaries = useMemo(() => {
    if (!summaries) return null;

    return summaries.flatMap((item, idx) => {
      const keyBase = `flight-prebook-product-summary-${idx}`;
      const nodes = [
        <FlightSummary
          {...item}
          key={keyBase}
          refundable={refundableByIndex[idx]}
        />,
      ];

      if (summaries.length - 1 !== idx) {
        nodes.push(
          <BMView key={`${keyBase}-divider`} style={styles.divider} />
        );
      }

      return nodes;
    });
  }, [summaries, refundableByIndex, styles.divider]);

  const passengersSummaries = useMemo(() => {
    if (!visible) return [];

    const passengers = Object.values(getValues().passengers).flat() || [];
    if (passengers.length === 0) return [];

    return passengers.flatMap((passenger, index) => {
      const keyBase = `flight-prebook-passenger-summary-${index}`;
      const baggage = extractBaggage(passenger, cr);

      return [
        <BMView key={keyBase} style={styles.passengerItem}>
          <PassengerSummaryItem
            index={index}
            baggage={baggage}
            passenger={passenger}
          />
        </BMView>,
        ...(passengers.length - 1 !== index
          ? [<BMView key={`${keyBase}-divider`} style={styles.divider} />]
          : []),
      ];
    });
  }, [visible, cr, getValues, styles.divider, styles.passengerItem]);

  return (
    <Fragment>
      <FullScreenDialog visible={visible}>
        <Header onClose={onClose} animate={animate} />
        <ScrollView
          onScroll={handleScroll}
          scrollEventThrottle={16}
          contentContainerStyle={[
            styles.scrollContent,
            { paddingBottom: insets.bottom },
          ]}
        >
          <BMView flex>
            <BMView style={[styles.container, styles.divider]} spacing="md">
              <BMText variant="uiLarge">
                {crf.confirmationModalFlightSummaryTitle}
              </BMText>
              <BMView style={styles.card}>{productSummaries}</BMView>
            </BMView>
            <BMView style={[styles.container]} spacing="md">
              <BMText variant="uiLarge">
                {crf.confirmationModalPassengerSummaryTitle}
              </BMText>
              <BMView style={styles.card}>{passengersSummaries}</BMView>
            </BMView>
          </BMView>
          <BMView style={styles.footer} spacing="lg">
            <BMView row align="center">
              <BMView flex>
                <BMText variant="uiNormalLi" ink="BMStyleTextInkSecondary">
                  {crf.confirmationModalPriceYouPay}
                </BMText>
                {priceYouPay ? (
                  <BMText variant="titleSmall" ink="BMStyleTextInkPrice">
                    {formatCurrency(convert(priceYouPay))}
                  </BMText>
                ) : (
                  <BMSkeletonLoader height={20} width={140} />
                )}
              </BMView>
              <BMView flex>
                <BMButton
                  label={crf.confirmationModalContinueCta}
                  stretchable
                  size="LARGE"
                  loading={!priceYouPay || isSubmittingBooking}
                  onPress={handleConfirm}
                />
              </BMView>
            </BMView>
            <BMText
              variant="paragraphSmall"
              ink="BMStyleTextInkSecondary"
              style={styles.textAlignCenter}
            >
              {crf.confirmationModalContinueInfo}
            </BMText>
          </BMView>
        </ScrollView>
      </FullScreenDialog>
      <ModalCompliantConfirmation
        visible={showNonCompliantModal}
        onConfirm={handleConfirmNonCompliant}
        onClose={() => setShowNonCompliantModal(false)}
        isLoading={!priceYouPay || isSubmittingBooking}
      />
    </Fragment>
  );
}

type Passengers =
  | AdultPassengerFormValue
  | ChildPassengerFormValue
  | InfantPassengerFormValue;

function extractBaggage(
  passenger: Passengers,
  cr: ContentGroup
): { label: string; value: string }[] {
  if (passenger.type === "INFANT" || !passenger.addOns?.length) return [];

  return passenger.addOns
    .flat()
    .flatMap(({ journeys }: FlightPassengerAddOns) =>
      Array.isArray(journeys) ? journeys : [journeys]
    )
    .map((segment: BaggageAddOns) => ({
      label: `${segment.departureAirport.airportCode} → ${segment.arrivalAirport.airportCode}`,
      value:
        Number(segment.baggageQuantity) > 1
          ? formatMessage(cr.baggagePieceUnit, {
              quantity: segment.baggageQuantity,
              weight: segment.baggageWeight,
            })
          : formatMessage(cr.baggageUnit, {
              num: segment.baggageWeight,
            }),
    }));
}

function createStyles(BMComponents: BMComponentsType) {
  return StyleSheet.create({
    container: {
      paddingVertical: BMPrimitives.BMSize.lg,
      paddingHorizontal: BMPrimitives.BMSize.md,
    },
    scrollContent: {
      flex: 1,
      paddingBottom: BMPrimitives.BMSize.lg,
    },
    divider: {
      borderBottomWidth: BMPrimitives.BMBorder.width.thick,
      borderBottomColor: BMComponents.BMStyleLineBordercolorSubtle,
    },
    card: {
      borderRadius: BMPrimitives.BMRadius.md,
      borderWidth: BMPrimitives.BMBorder.width.thick,
      borderColor: BMComponents.BMStyleLineBordercolorSubtle,
      backgroundColor: BMComponents.BMStyleSurfaceBgcolor,
      overflow: "hidden",
    },
    passengerItem: {
      padding: BMPrimitives.BMSize.md,
    },
    footer: {
      borderTopWidth: BMPrimitives.BMBorder.width.thick,
      borderTopColor: BMComponents.BMStyleLineBordercolorSubtle,
      paddingVertical: BMPrimitives.BMSize.lg,
      paddingHorizontal: BMPrimitives.BMSize.md,
    },
    textAlignCenter: {
      textAlign: "center",
    },
  });
}
